// "Go live" for a placeholder hosting site — Loot → Products → a hosting row
// still named tmp-*.hosting.squish.internal / vps-*.squish.internal.
//
// The customer does this from the dashboard's "Set domain". Staff did it only
// by impersonating; this is the same operation from the CRM. Type the domain,
// read exactly what will happen, Go live. The backend checks the site's owner
// is on this account and the domain is theirs to use, then renames the site,
// points DNS and issues SSL, and logs it to Events against the staffer.

import { useState } from 'react';
import { setHostingDomain } from '../api';
import { normaliseGoLiveDomain } from '../lib/hostingGoLive';

const btn = { padding: '4px 10px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' };
const primary = { ...btn, background: 'var(--accent)', color: '#fff', border: 'none' };
const input = { padding: '5px 7px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 12, fontFamily: 'var(--mono)', outline: 'none', width: 260 };

export default function HostingGoLive({ accountId, placeholder, onDone }) {
  const [raw, setRaw] = useState('');
  const [confirming, setConfirming] = useState(null);   // normalised domain
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  function review() {
    const { domain, error: err } = normaliseGoLiveDomain(raw);
    setError(err);
    if (!err) setConfirming(domain);
  }

  async function goLive() {
    setBusy(true); setError('');
    try {
      const r = await setHostingDomain(accountId, placeholder, confirming);
      setResult(r);
      setConfirming(null);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  }

  if (result) {
    const records = Array.isArray(result.aRecords) ? result.aRecords : [];
    return (
      <div style={{ padding: '8px 0 12px', fontSize: 12 }}>
        <div style={{ color: '#4ade80', fontFamily: 'var(--mono)', fontSize: 11, marginBottom: 6 }}>
          ✓ {result.previousDomain || placeholder} is now {result.domain}. Logged to this account's Events.
        </div>
        {result.externalRegistrar && records.length > 0 && (
          <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24', fontSize: 11 }}>
            {result.domain} is registered somewhere else, so it won't load until the customer points it here. At their registrar, set:
            <div style={{ fontFamily: 'var(--mono)', marginTop: 4, color: 'var(--text)' }}>
              {records.map(r => <div key={`${r.type}:${r.host}`}>{r.type} {r.host} → {r.value}</div>)}
            </div>
          </div>
        )}
        <button style={{ ...btn, marginTop: 8 }} onClick={() => onDone?.()}>Refresh products</button>
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0 12px', fontSize: 12 }}>
      <div style={{ fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 6 }}>Go live</div>
      {!confirming ? (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={raw} onChange={e => setRaw(e.target.value)} placeholder="customer's domain, e.g. example.com" style={input} aria-label="Customer domain" />
          <button data-write style={btn} disabled={busy || !raw.trim()} onClick={review}>Review</button>
        </div>
      ) : (
        <div role="alertdialog" aria-label="Confirm go live" style={{ padding: '10px 12px', borderRadius: 6, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.3)' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Put this site live on {confirming}?</div>
          <div style={{ color: 'var(--text2)' }}>
            The site moves from <span style={{ fontFamily: 'var(--mono)' }}>{placeholder}</span> to <span style={{ fontFamily: 'var(--mono)' }}>{confirming}</span>.
            {' '}If we hold the domain, its DNS is pointed here and SSL is issued automatically; if it's registered elsewhere you'll get the records to give the customer.
            {' '}This can take up to a minute.
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button data-write style={primary} disabled={busy} onClick={goLive}>{busy ? 'Going live…' : 'Go live'}</button>
            <button style={btn} disabled={busy} onClick={() => setConfirming(null)}>Back</button>
          </div>
        </div>
      )}
      {error && <div style={{ color: '#f87171', fontSize: 11, marginTop: 6 }}>⚠ {error}</div>}
    </div>
  );
}
